import React, { useState, useEffect } from 'react';
import { Button, Form, Input, message, Switch, Space, Card } from 'antd';
import { MailOutlined, UserOutlined } from '@ant-design/icons';
import { httpsCallable } from 'firebase/functions';
import { functions, auth } from '../../firebase';

const { TextArea } = Input;

const EmailTester = ({ onClose, defaultValues = {} }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [isHtml, setIsHtml] = useState(true);
  const [userEmail, setUserEmail] = useState('');

  useEffect(() => {
    if (Object.keys(defaultValues).length > 0) {
      form.setFieldsValue(defaultValues);
    }
    
    const user = auth.currentUser;
    if (user?.email) {
      setUserEmail(user.email);
      if (!defaultValues.from) {
        form.setFieldsValue({ from: user.email });
      }
    }
  }, [defaultValues, form]);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      console.log('Form values:', {
        ...values,
        content: values.content ? '[content exists]' : '[no content]',
        contentLength: values.content?.length || 0,
        isHtml
      });
      
      if (!values.to) {
        console.error('No "to" field in form values');
        throw new Error('Recipient email is required');
      }
      
      if (!values.content) {
        console.error('No content provided in the message field');
        throw new Error('Message content is required');
      }
      
      const sendEmailFunction = httpsCallable(functions, 'sendEmail', { timeout: 60000 });
      
      // Prepare the email data object
      const emailData = {
        to: values.to.split(',').map(email => email.trim()),
        from: values.from || userEmail,
        subject: values.subject || 'No Subject',
        [isHtml ? 'html' : 'text']: values.content,
        eventType: values.eventType || 'user_triggered_email',
      };
      
      console.log('Email data prepared:', {
        ...emailData,
        [isHtml ? 'html' : 'text']: '[content hidden]',
        contentType: isHtml ? 'html' : 'text'
      });

      // Add optional fields if they exist
      if (values.cc) emailData.cc = values.cc.split(',').map(email => email.trim());
      if (values.bcc) emailData.bcc = values.bcc.split(',').map(email => email.trim());
      if (values.replyTo) emailData.replyTo = values.replyTo;

      // Log the final email data structure
      console.log('Final email data structure:', {
        to: emailData.to,
        from: emailData.from,
        subject: emailData.subject,
        hasContent: !!(emailData.html || emailData.text),
        contentType: emailData.html ? 'html' : 'text',
        contentLength: (emailData.html || emailData.text || '').length,
        eventType: emailData.eventType,
        cc: emailData.cc,
        bcc: emailData.bcc,
        replyTo: emailData.replyTo
      });
      
      // Call the Firebase function with the email data as the direct parameter
      // (not wrapped in a data property, as the callable function will handle that)
      console.log('Calling sendEmail function with data:', {
        ...emailData,
        [emailData.html ? 'html' : 'text']: '[content hidden]'
      });
      
      const result = await sendEmailFunction(emailData);
      console.log('Email sent successfully:', result);
      
      message.success({
        content: (
          <>
            <p>Email sent successfully!</p>
            {result.data?.messageId && (
              <p className="text-xs mt-1">Message ID: {result.data.messageId}</p>
            )}
          </>
        ),
        duration: 5,
      });
      
      // Only reset if this is a standalone form
      if (onClose) {
        form.resetFields(['to', 'cc', 'bcc', 'subject', 'content']);
      }
      
      return result;
    } catch (error) {
      console.error('Error sending email:', {
        error,
        message: error.message,
        code: error.code,
        details: error.details,
        stack: error.stack
      });
      
      let errorMessage = 'Failed to send email';
      if (error.details) {
        errorMessage += `: ${error.details}`;
      } else if (error.message) {
        errorMessage += `: ${error.message}`;
      }
      
      message.error(errorMessage);
      throw error; // Re-throw to allow parent components to handle the error
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card 
      title={
        <div className="flex items-center">
          <MailOutlined className="mr-2" />
          <span>Email Composer</span>
        </div>
      }
      className="max-w-4xl mx-auto"
      extra={
        <Button 
          type="text" 
          onClick={onClose}
          icon={<UserOutlined />}
          className="flex items-center"
        >
          {userEmail || 'Not signed in'}
        </Button>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          isHtml: true,
          from: userEmail,
          eventType: 'user_triggered_email'
        }}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Form.Item
            label="From"
            name="from"
            rules={[{ 
              required: true, 
              type: 'email',
              message: 'Please input a valid email address!' 
            }]}
          >
            <Input 
              prefix={<MailOutlined className="text-gray-400" />} 
              placeholder="sender@example.com" 
            />
          </Form.Item>
          
          <Form.Item
            label="Event Type"
            name="eventType"
            tooltip="Used for tracking and analytics"
          >
            <Input placeholder="e.g., welcome_email, notification" />
          </Form.Item>
        </div>
        
        <Form.Item
          label="To"
          name="to"
          rules={[{ 
            required: true, 
            message: 'Please enter at least one recipient!',
            validator: (_, value) => {
              const emails = value ? value.split(',').map(e => e.trim()) : [];
              const valid = emails.every(email => 
                /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
              );
              return valid ? Promise.resolve() : Promise.reject('Please enter valid email addresses separated by commas');
            }
          }]}
        >
          <Input.TextArea 
            rows={2} 
            placeholder="recipient1@example.com, recipient2@example.com" 
          />
        </Form.Item>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Form.Item 
            label="CC (optional)" 
            name="cc"
            rules={[{
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                const emails = value.split(',').map(e => e.trim());
                const valid = emails.every(email => 
                  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
                );
                return valid ? Promise.resolve() : Promise.reject('Please enter valid email addresses separated by commas');
              }
            }]}
          >
            <Input placeholder="cc1@example.com, cc2@example.com" />
          </Form.Item>
          
          <Form.Item 
            label="BCC (optional)" 
            name="bcc"
            rules={[{
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                const emails = value.split(',').map(e => e.trim());
                const valid = emails.every(email => 
                  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
                );
                return valid ? Promise.resolve() : Promise.reject('Please enter valid email addresses separated by commas');
              }
            }]}
          >
            <Input placeholder="bcc@example.com" />
          </Form.Item>
        </div>
        
        <Form.Item
          label="Subject"
          name="subject"
          rules={[{ required: true, message: 'Please enter email subject' }]}
        >
          <Input placeholder="Email subject" />
        </Form.Item>

        <Form.Item
          label="Message"
          name="content"
          rules={[{ required: true, message: 'Please enter your message content!' }]}
        >
          <TextArea 
            rows={10} 
            placeholder={
              isHtml 
                ? 'Write your HTML message here...' 
                : 'Write your plain text message here...'
            }
            style={{ fontFamily: isHtml ? 'monospace' : 'inherit' }}
          />
        </Form.Item>
        
        <div className="flex justify-between items-center">
          <Form.Item name="isHtml" valuePropName="checked" noStyle>
            <Switch
              checkedChildren="HTML"
              unCheckedChildren="Plain Text"
              checked={isHtml}
              onChange={setIsHtml}
              className="mr-4"
            />
          </Form.Item>
          
          <Space>
            <Button onClick={onClose}>
              Cancel
            </Button>
            <Button 
              type="primary" 
              htmlType="submit"
              icon={<MailOutlined />} 
              loading={loading}
            >
              Send Email
            </Button>
          </Space>
        </div>
      </Form>
    </Card>
  );
};

export default EmailTester;
